import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, api } from "../lib/api.js";
import { TextField, Button, Banner, COLORS as C } from "../components/UI.jsx";

export default function PickupAddresses() {
  const navigate = useNavigate();
  if (!auth.loggedIn()) { navigate("/login", { replace: true }); return null; }

  const [addrs, setAddrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get("/pickups/addresses");
      setAddrs(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setAsDefault = async (id) => {
    try {
      await api.patch(`/pickups/addresses/${id}`, { is_default: true });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this pickup address?")) return;
    try {
      await api.delete(`/pickups/addresses/${id}`);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: C.cream, fontFamily: "-apple-system, system-ui, Inter, sans-serif" }}>
      {/* Nav */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.line}`, padding: "16px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/dashboard" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, background: C.red, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontWeight: 800, fontSize: 16 }}>S</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.navy }}>Shiftswift</div>
          </Link>
          <Link to="/dashboard" style={{ fontSize: 13, color: C.text2, textDecoration: "none", fontWeight: 600 }}>← Back to dashboard</Link>
        </div>
      </div>

      {/* Header */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "32px 24px 16px" }}>
        <div style={{ fontSize: 11, color: C.red, letterSpacing: 1.5, fontWeight: 700, marginBottom: 8 }}>PICKUP ADDRESSES</div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -0.8 }}>
          Where should we pick up from?
        </h1>
        <p style={{ fontSize: 15, color: C.text2, marginTop: 8, lineHeight: 1.6 }}>
          Add your warehouse, store, or office. India Post will pick up from the address you choose for each shipment.
        </p>
      </div>

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px 60px" }}>
        {error && <Banner kind="error" onDismiss={() => setError(null)}>{error}</Banner>}

        {/* Add new */}
        {!showForm && (
          <div style={{ marginBottom: 24 }}>
            <Button onClick={() => { setEditing(null); setShowForm(true); }} fullWidth={false}>
              + Add new pickup address
            </Button>
          </div>
        )}

        {showForm && (
          <AddressForm
            initial={editing}
            onCancel={() => { setShowForm(false); setEditing(null); }}
            onSaved={() => { setShowForm(false); setEditing(null); load(); }}
          />
        )}

        {/* List */}
        {loading ? (
          <div style={{ padding: 60, textAlign: "center", color: C.text3 }}>Loading…</div>
        ) : addrs.length === 0 && !showForm ? (
          <div style={{
            background: C.white, border: `2px dashed ${C.border}`, borderRadius: 10,
            padding: "48px 24px", textAlign: "center",
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 6 }}>
              No pickup addresses yet
            </div>
            <div style={{ fontSize: 13, color: C.text3 }}>
              Add your first one above to start booking shipments.
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {addrs.map((a) => (
              <div key={a.id} style={{
                background: C.white, borderRadius: 10,
                border: a.is_default ? `2px solid ${C.green}` : `1px solid ${C.line}`,
                padding: 20, display: "flex", justifyContent: "space-between", gap: 16,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.navy }}>
                      {a.label}
                    </div>
                    {a.is_default && (
                      <span style={{
                        background: C.green, color: C.white,
                        padding: "2px 8px", borderRadius: 12, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                      }}>DEFAULT</span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: C.text2, lineHeight: 1.6 }}>
                    {a.contact_name} · {a.phone}
                  </div>
                  <div style={{ fontSize: 13, color: C.text2, marginTop: 4 }}>
                    {a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ""}<br />
                    {a.city}, {a.state} – {a.pincode}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {!a.is_default && (
                    <button onClick={() => setAsDefault(a.id)} style={{
                      background: "transparent", color: C.green,
                      border: `1px solid ${C.green}`, padding: "6px 12px",
                      borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                      fontFamily: "inherit",
                    }}>
                      Set default
                    </button>
                  )}
                  <button onClick={() => { setEditing(a); setShowForm(true); }} style={{
                    background: "transparent", color: C.text2,
                    border: `1px solid ${C.border}`, padding: "6px 12px",
                    borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    fontFamily: "inherit",
                  }}>
                    Edit
                  </button>
                  {!a.is_default && (
                    <button onClick={() => remove(a.id)} style={{
                      background: "transparent", color: "#991B1B",
                      border: "none", padding: "6px 12px",
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                      fontFamily: "inherit",
                    }}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AddressForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(initial || {
    label: "", contact_name: "", phone: "",
    address_line1: "", address_line2: "",
    city: "", state: "", pincode: "",
    is_default: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError(null);
    if (!form.label.trim()) return setError("Give this address a name (e.g. Main Warehouse).");
    if (!form.contact_name.trim()) return setError("Contact name is required.");
    if (!form.phone.trim()) return setError("Phone number is required.");
    if (!form.address_line1.trim()) return setError("Address is required.");
    if (!form.city.trim()) return setError("City is required.");
    if (!form.state.trim()) return setError("State is required.");
    if (!/^\d{6}$/.test(form.pincode)) return setError("PIN code must be 6 digits.");
    setBusy(true);
    try {
      // Normalize phone
      let phone = form.phone.trim().replace(/\s/g, "").replace(/-/g, "");
      if (!phone.startsWith("+")) {
        phone = phone.length === 10 ? `+91${phone}` : phone.startsWith("91") ? `+${phone}` : phone;
      }
      const payload = { ...form, phone };
      if (initial) {
        await api.patch(`/pickups/addresses/${initial.id}`, payload);
      } else {
        await api.post("/pickups/addresses", payload);
      }
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      background: C.white, borderRadius: 10, border: `1px solid ${C.line}`,
      padding: 24, marginBottom: 24,
    }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: C.navy, marginBottom: 16 }}>
        {initial ? "Edit pickup address" : "Add new pickup address"}
      </div>

      {error && <Banner kind="error" onDismiss={() => setError(null)}>{error}</Banner>}

      <TextField label="Address name" value={form.label} onChange={upd("label")}
        placeholder="e.g. Main Warehouse, Mumbai Office" required autoFocus
        hint="A short name to identify this address — only you see it" />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <TextField label="Contact name" value={form.contact_name} onChange={upd("contact_name")}
          placeholder="Person India Post should ask for" required />
        <TextField label="Phone" value={form.phone} onChange={upd("phone")} format="phone"
          placeholder="98765 43210" required />
      </div>

      <TextField label="Address line 1" value={form.address_line1} onChange={upd("address_line1")}
        placeholder="Building, street" required />
      <TextField label="Address line 2 (optional)" value={form.address_line2} onChange={upd("address_line2")}
        placeholder="Floor, area, landmark" />

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
        <TextField label="City" value={form.city} onChange={upd("city")}
          placeholder="Mumbai" required />
        <TextField label="State" value={form.state} onChange={upd("state")}
          placeholder="Maharashtra" required />
        <TextField label="PIN code" value={form.pincode} onChange={upd("pincode")} format="pin"
          placeholder="400001" maxLength={6} required onEnter={submit} />
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, marginBottom: 16, cursor: "pointer" }}>
        <input
          type="checkbox" checked={!!form.is_default}
          onChange={(e) => upd("is_default")(e.target.checked)}
        />
        <span style={{ fontSize: 13, color: C.text2 }}>Use this as my default pickup address</span>
      </label>

      <div style={{ display: "flex", gap: 10 }}>
        <Button onClick={onCancel} variant="secondary" fullWidth={false}>Cancel</Button>
        <Button onClick={submit} busy={busy} fullWidth={false} style={{ flex: 1 }}>
          {busy ? "Saving…" : initial ? "Save changes" : "Add address"}
        </Button>
      </div>
    </div>
  );
}
